import "./index.css";
import { clsx } from "clsx";
import { useCallback, useState } from "react";
import { useTranslations } from "../../i18n";
import { Button } from "../Button/Button";
import { Modal } from "../Modal";
import { Toast } from "../Toast/Toast";
import type { IConfirmDialogProps } from "./types";

export const ConfirmDialog = ({
  onDismiss,
  title,
  subtitle,
  submitText,
  submitColor = "brand",
  submitAction,
  cancelText,
  cancelAction,
  errorTitle,
  errorMessage,
  onResolve,
  onError,
  className,
  showClose = false,
  drawerOnMobile,
}: IConfirmDialogProps) => {
  const { t } = useTranslations();
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(() => {
    setLoading(true);
    submitAction()
      .then(() => {
        if (onResolve) {
          onResolve();
        } else {
          onDismiss();
        }
      })
      .catch(() => {
        if (onError) {
          onError();
        } else if (errorTitle) {
          Toast.error({ title: errorTitle, message: errorMessage });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [errorMessage, errorTitle, onDismiss, onError, onResolve, submitAction]);

  return (
    <Modal
      open
      onOpenChange={onDismiss}
      closeOnOutsidePress={false}
      drawerOnMobile={drawerOnMobile}
      className={clsx("nuco-confirm-dialog", className)}
    >
      <Modal.Header
        title={title}
        description={subtitle}
        showClose={showClose}
      />
      <Modal.Footer>
        <Button
          variant="soft"
          color="neutral"
          onClick={cancelAction ?? onDismiss}
        >
          {cancelText ?? t("ui.confirmDialog.cancel")}
        </Button>
        <Button color={submitColor} loading={loading} onClick={handleSubmit}>
          {submitText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
